import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Briefcase } from 'lucide-react';

const AboutPage = () => {
  const team = [
    {
      name: "Isabella Chen",
      role: "Creative Director & Lead Designer",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      bio: "With over 15 years of experience in haute couture, Isabella leads our creative vision with innovative designs and meticulous attention to detail."
    },
    {
      name: "Marcus Thompson",
      role: "Sustainable Fashion Lead",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      bio: "Marcus specializes in sustainable fashion design, bringing eco-conscious innovation to our collections while maintaining luxury standards."
    },
    {
      name: "Sofia Rodriguez",
      role: "Head of Textile Design",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      bio: "Sofia's expertise in textile innovation and artisanal techniques brings unique character to our fashion collections."
    }
  ];

  const awards = [
    "Fashion Designer of the Year 2024",
    "Sustainable Fashion Innovation Award",
    "Best Luxury Bridal Collection 2023"
  ];

  const clients = [
    "International Fashion Houses",
    "Celebrity Stylists",
    "Luxury Boutiques",
    "High-Profile Private Clients"
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Our Story</h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Crafting timeless elegance through innovative fashion design and sustainable practices.
          </p>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-16 bg-black/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Text Card */}
            <div className="bg-gray-900 rounded-xl shadow-lg p-8 flex flex-col justify-center h-full">
              <h2 className="text-3xl font-bold mb-6 text-emerald-400">Who We Are</h2>
              <p className="text-gray-300 text-lg mb-4">
                Advance Elegance Studio is a passionate team of designers, artisans, and visionaries dedicated to redefining modern luxury fashion in Pakistan and beyond. Founded on the principles of creativity, sustainability, and uncompromising quality, we believe every client deserves to feel extraordinary.
              </p>
              <p className="text-gray-400 text-base">
                From our humble beginnings in Sargodha to becoming a trusted name for bespoke couture, we have always put our clients' dreams at the heart of our work. Our studio blends traditional craftsmanship with contemporary design, ensuring every piece tells a unique story. Whether you seek a show-stopping bridal ensemble or a statement ready-to-wear look, we are here to bring your vision to life with elegance and care.
              </p>
            </div>
            {/* Image Card */}
            <div className="bg-gray-900 rounded-xl shadow-lg flex items-center justify-center h-full min-h-[320px]">
              <img
                src="https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=800&q=80"
                alt="Advance Elegance Studio Team"
                className="object-cover rounded-lg w-full h-full max-h-[400px]"
                style={{ minHeight: '320px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Our Mission</h2>
              <p className="text-gray-400 mb-6">
                At Advance Elegance Studio, we're dedicated to pushing the boundaries of fashion design while maintaining the highest standards of craftsmanship and sustainability. Our mission is to create timeless pieces that not only make our clients feel extraordinary but also contribute to a more sustainable fashion industry.
              </p>
              <p className="text-gray-400">
                We believe in the power of innovative design to transform not just how people look, but how they feel. Every piece we create is a testament to our commitment to excellence, sustainability, and the artistry of fashion design.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="p-6 bg-gray-900 rounded-lg">
                <h3 className="text-3xl font-bold text-emerald-400 mb-2">15+</h3>
                <p className="text-gray-400">Years of Excellence</p>
              </div>
              <div className="p-6 bg-gray-900 rounded-lg">
                <h3 className="text-3xl font-bold text-emerald-400 mb-2">500+</h3>
                <p className="text-gray-400">Happy Clients</p>
              </div>
              <div className="p-6 bg-gray-900 rounded-lg">
                <h3 className="text-3xl font-bold text-emerald-400 mb-2">50+</h3>
                <p className="text-gray-400">Fashion Shows</p>
              </div>
              <div className="p-6 bg-gray-900 rounded-lg">
                <h3 className="text-3xl font-bold text-emerald-400 mb-2">100%</h3>
                <p className="text-gray-400">Client Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-black/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-16 text-center">Meet Our Team</h2>
          <div className="grid md:grid-cols-3 gap-12">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="relative mb-6 overflow-hidden rounded-lg">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full aspect-[3/4] object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 p-6">
                      <p className="text-gray-300">{member.bio}</p>
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                <p className="text-emerald-400">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-center text-white">Why Choose Us</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-gray-900 rounded-xl shadow-lg p-8 flex flex-col items-center text-center">
              <span className="text-3xl mb-4">🌟</span>
              <h3 className="text-xl font-bold mb-2 text-white">Unmatched Craftsmanship</h3>
              <p className="text-gray-400">Every piece is meticulously crafted by skilled artisans, ensuring the highest quality and attention to detail.</p>
            </div>
            <div className="bg-gray-900 rounded-xl shadow-lg p-8 flex flex-col items-center text-center">
              <span className="text-3xl mb-4">🌱</span>
              <h3 className="text-xl font-bold mb-2 text-white">Sustainable Fashion</h3>
              <p className="text-gray-400">We are committed to eco-friendly practices, using sustainable materials and processes in all our collections.</p>
            </div>
            <div className="bg-gray-900 rounded-xl shadow-lg p-8 flex flex-col items-center text-center">
              <span className="text-3xl mb-4">🤝</span>
              <h3 className="text-xl font-bold mb-2 text-white">Personalized Experience</h3>
              <p className="text-gray-400">We work closely with every client to bring their unique vision to life, offering bespoke solutions and attentive service.</p>
            </div>
            <div className="bg-gray-900 rounded-xl shadow-lg p-8 flex flex-col items-center text-center">
              <span className="text-3xl mb-4">🏆</span>
              <h3 className="text-xl font-bold mb-2 text-white">Award-Winning Design</h3>
              <p className="text-gray-400">Recognized for excellence, our studio has received multiple awards for innovation and luxury in fashion design.</p>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default AboutPage;